/**
 * Advisor trial and seat entitlement (Firm-Ready v1, FR11, #1868).
 *
 * Lives beside the edge functions because the Stripe webhook is Deno and cannot import from `src/`; the web
 * re-exports it from `src/lib/billing/advisorTrial.ts`, the same way the production gate does.
 *
 * Why it exists: the signup form and the pricing page both promised "14-day free trial, no credit card
 * required", and a brand-new advisor had no `advisor_seat_balance` row at all — so `available` was 0, Send
 * Invitation was disabled, and the first thing a new customer met was a paywall contradicting the sentence
 * they had just read. `create-advisor-subscription` was already asking Stripe for `trial_period_days: 14`;
 * nothing turned a trialing subscription into seats.
 *
 * The decision taken 2026-10-06 was trial WITH a card on file: fourteen days of the plan the firm chose,
 * auto-converting unless they cancel. So a trial is not a special mode — it is the plan, early, and this
 * module exists to say that in one place that both the webhook and the UI can read.
 *
 * Pure: it decides, it does not read a database and it does not call Stripe.
 */

export type AdvisorTier = 'starter' | 'professional' | 'practice';

/** Active cases each plan includes. Mirrors `create-advisor-subscription`'s tier configs. */
export const TIER_SEATS: Record<AdvisorTier, number> = {
  starter: 5,
  professional: 15,
  practice: 30,
};

export const TRIAL_DAYS = 14;

export interface SubscriptionState {
  /** Stripe's subscription status. */
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete' | 'incomplete_expired';
  tier: AdvisorTier;
}

/**
 * Statuses that carry the plan's seats.
 *
 * `past_due` is deliberately included: a card that failed on renewal is a dunning problem, and pulling a
 * firm's active cases out from under them mid-matter because an expiry date rolled over is not a billing
 * policy anybody would choose. Stripe moves the subscription to `unpaid` or `canceled` when dunning gives up,
 * and that is where the seats go.
 *
 * `incomplete` is excluded: the card has not been accepted yet, so nothing has been agreed.
 */
const ENTITLED: readonly string[] = ['trialing', 'active', 'past_due'];

/** Seats this subscription entitles its advisor to right now. Unknown status → nothing, never everything. */
export function entitledSeats(sub: SubscriptionState): number {
  if (!ENTITLED.includes(sub.status)) return 0;
  return TIER_SEATS[sub.tier] ?? 0;
}

/**
 * How many seats to credit (positive) or take back (negative) when a subscription moves from one state to
 * another. `null` means there was no subscription before.
 *
 * Expressed as a difference rather than an absolute so a replayed webhook — Stripe retries, and will happily
 * deliver the same event twice — cannot double-credit: the same state in and out is always zero.
 */
export function seatDelta(before: SubscriptionState | null, after: SubscriptionState | null): number {
  const had = before ? entitledSeats(before) : 0;
  const has = after ? entitledSeats(after) : 0;
  return has - had;
}

/** When a trial that starts now runs out. */
export function trialEndsAt(start: Date): Date {
  return new Date(start.getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000);
}

/** Days left in a trial, floored at zero, for the banner that tells a firm where it stands. */
export function trialDaysLeft(trialEnd: Date, now: Date): number {
  return Math.max(0, Math.ceil((trialEnd.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)));
}

export interface SeatSources {
  /** The firm's live subscription, if it has one. `seats_used` is the cases already open against it. */
  subscription: (SubscriptionState & { seats_used?: number | null }) | null;
  /** The older purchased-seat ledger, for firms that bought packs before subscriptions existed. */
  legacy: { total_purchased: number | null; total_active: number | null; total_pending: number | null } | null;
}

export interface SeatAvailability {
  limit: number;
  used: number;
  available: number;
  /** Which ledger answered, so a screen can explain itself rather than just show a number. */
  source: 'subscription' | 'legacy' | 'none';
}

/**
 * How many more cases a firm may open.
 *
 * A subscription wins when it entitles anything, because its seats and any purchased pack are not additive —
 * reading both and summing them was never the intent, and reading only the legacy table is what made a firm
 * on a live trial unable to open a single case (#1868). A subscription that entitles nothing (cancelled,
 * unpaid, still incomplete) falls through to whatever the firm actually bought.
 */
export function availableSeats(sources: SeatSources): SeatAvailability {
  const entitled = sources.subscription ? entitledSeats(sources.subscription) : 0;
  if (entitled > 0) {
    const used = sources.subscription?.seats_used ?? 0;
    return { limit: entitled, used, available: Math.max(0, entitled - used), source: 'subscription' };
  }

  if (sources.legacy) {
    const limit = sources.legacy.total_purchased ?? 0;
    const used = (sources.legacy.total_active ?? 0) + (sources.legacy.total_pending ?? 0);
    return { limit, used, available: Math.max(0, limit - used), source: 'legacy' };
  }

  return { limit: 0, used: 0, available: 0, source: 'none' };
}

/** The plan every pricing CTA pre-selects, and the fallback when the URL says nothing usable. */
export const DEFAULT_TIER: AdvisorTier = 'professional';

/**
 * The tier a signup URL is asking for.
 *
 * Every pricing CTA sends `?plan=`, and signup never read it — so a firm that clicked Practice was signed up
 * with no plan at all, and no subscription was ever created (#1860). Unknown values fall back rather than
 * being trusted: this parameter comes from a URL anyone can edit.
 */
export function planFromQuery(params: URLSearchParams): AdvisorTier {
  const raw = (params.get('plan') ?? '').trim().toLowerCase();
  return (Object.keys(TIER_SEATS) as AdvisorTier[]).includes(raw as AdvisorTier) ? (raw as AdvisorTier) : DEFAULT_TIER;
}

/**
 * Stripe's subscription status, as this system records it.
 *
 * Three corrections to the map this replaces (#1868):
 *  - `unpaid` used to fold into `past_due`. `past_due` keeps its seats deliberately, so that kept granting a
 *    full plan to a firm whose dunning had already given up. `unpaid` is its own status and entitles nothing.
 *  - an unrecognised status used to fall back to `active`, which granted a full plan to a status nobody had
 *    ever seen. It falls back to `canceled` now: the safe side of a billing question is the one that does not
 *    hand out seats.
 *  - `paused` had no meaning here at all; a paused subscription is not paying for open cases.
 */
export function mapStripeStatus(stripeStatus: string): SubscriptionState['status'] {
  const map: Record<string, SubscriptionState['status']> = {
    trialing: 'trialing',
    active: 'active',
    past_due: 'past_due',
    unpaid: 'unpaid',
    canceled: 'canceled',
    incomplete: 'incomplete',
    incomplete_expired: 'incomplete_expired',
    paused: 'canceled',
  };
  return map[stripeStatus] ?? 'canceled';
}
